import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Tabs, Tab, InputGroup } from 'react-bootstrap';
import { Link, CalendarPlus } from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

const SubscriptionModal = ({ show, onHide, onSubscriptionAdded }) => {
  const [activeTab, setActiveTab] = useState('subscribed');
  const [calendarUrl, setCalendarUrl] = useState('');
  const [calendarName, setCalendarName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#FF6B6B');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Vibrant color palette for subscribed calendars
  const subscriptionColors = [
    '#FF6B6B', // Vibrant Red
    '#4ECDC4', // Vibrant Teal
    '#45B7D1', // Vibrant Blue
    '#96CEB4', // Vibrant Green
    '#FFEAA7', // Vibrant Yellow
    '#DDA0DD', // Vibrant Plum
    '#FF8C42', // Vibrant Orange
    '#A8E6CF', // Vibrant Mint
    '#FF69B4', // Vibrant Pink
    '#9370DB', // Vibrant Purple
    '#20B2AA', // Vibrant Sea Green
    '#FF6347', // Vibrant Tomato
    '#32CD32', // Vibrant Lime Green
    '#FFD700', // Vibrant Gold
    '#FF1493', // Vibrant Deep Pink
    '#00CED1', // Vibrant Dark Turquoise
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      // Validate inputs
      if (!calendarName.trim()) {
        throw new Error('Please enter a calendar name');
      }

      if ((activeTab === 'url' || activeTab === 'subscribed') && !calendarUrl.trim()) {
        throw new Error('Please enter a calendar URL');
      }

      // Get authentication token
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Create subscription via API
      const response = await axios.post(`${API_ENDPOINTS.CALENDAR_SUBSCRIPTIONS}`, {
        name: calendarName,
        status: 'active',
        type: activeTab === 'subscribed' ? 'url' : activeTab,
        url: (activeTab === 'url' || activeTab === 'subscribed') ? calendarUrl : null,
        color: selectedColor
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      onSubscriptionAdded(response.data);
      setSuccess('Calendar subscription added successfully!');
      
      // Reset form
      setCalendarName('');
      setCalendarUrl('');
      setActiveTab('subscribed');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    // This would integrate with Google Calendar API
    setSuccess('Redirecting to Google Calendar authorization...');
    // In a real implementation, this would open Google OAuth
    setTimeout(() => {
      setSuccess('Google Calendar connected! Please enter a name for this calendar.');
    }, 2000);
  };

  const handleOutlookAuth = () => {
    // This would integrate with Microsoft Graph API
    setSuccess('Redirecting to Microsoft Calendar authorization...');
    // In a real implementation, this would open Microsoft OAuth
    setTimeout(() => {
      setSuccess('Outlook Calendar connected! Please enter a name for this calendar.');
    }, 2000);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="d-flex align-items-center gap-2"><CalendarPlus/>
          <span className="d-none d-md-inline ms-2">Add Calendar Subscription</span>
          </div>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab eventKey="subscribed" title={
            <span>
              <div className="d-flex align-items-center gap-2"><Link/>
              <span className="d-none d-md-inline ms-2">Subscribed to Calendar</span>
              </div>
            </span>
          }>
            <div className="p-3">
              <p className="text-muted mb-3">
                Subscribe to someone else's calendar using their share link or iCal feed.
              </p>
              
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Calendar Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., John's Work Calendar, Team Events"
                    value={calendarName}
                    onChange={(e) => setCalendarName(e.target.value)}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Calendar URL or Share Link</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://example.com/calendar.ics or share link"
                    value={calendarUrl}
                    onChange={(e) => setCalendarUrl(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    Enter the iCal feed URL or calendar share link
                  </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Calendar Color</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {subscriptionColors.map((color, index) => (
                      <div
                        key={index}
                        className="color-option"
                        style={{
                          backgroundColor: color,
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          border: selectedColor === color ? '3px solid #333' : '2px solid rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => setSelectedColor(color)}
                        title={`Color ${index + 1}`}
                      ></div>
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    Choose a color to distinguish this calendar from others
                  </Form.Text>
                </Form.Group>
                
                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Adding...' : 'Subscribe to Calendar'}
                  </Button>
                </div>
              </Form>
            </div>
          </Tab>

          <Tab eventKey="google" title={
            <span>
              <i className="bi bi-google me-1" />
              Google Calendar
            </span>
          }>
            <div className="p-3">
              <p className="text-muted mb-3">
                Connect your Google Calendar to sync events automatically.
              </p>
              
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Calendar Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Work Calendar, Personal Events"
                    value={calendarName}
                    onChange={(e) => setCalendarName(e.target.value)}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Calendar Color</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {subscriptionColors.map((color, index) => (
                      <div
                        key={index}
                        className="color-option"
                        style={{
                          backgroundColor: color,
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          border: selectedColor === color ? '3px solid #333' : '2px solid rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => setSelectedColor(color)}
                        title={`Color ${index + 1}`}
                      ></div>
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    Choose a color to distinguish this calendar from others
                  </Form.Text>
                </Form.Group>
                
                <div className="d-grid gap-2">
                  <Button
                    variant="outline-primary"
                    onClick={handleGoogleAuth}
                    disabled={isLoading}
                  >
                    <i className="bi bi-google"></i>
                    <span className="d-none d-md-inline ms-2">Connect Google Calendar</span>
                  </Button>
                </div>
              </Form>
            </div>
          </Tab>

          <Tab eventKey="outlook" title={
            <span>
              <i className="bi bi-microsoft"></i>
              <span className="d-none d-md-inline ms-2">Outlook Calendar</span>
            </span>
          }>
            <div className="p-3">
              <p className="text-muted mb-3">
                Connect your Outlook/Microsoft 365 Calendar to sync events automatically.
              </p>
              
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Calendar Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Office Calendar, Team Events"
                    value={calendarName}
                    onChange={(e) => setCalendarName(e.target.value)}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Calendar Color</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {subscriptionColors.map((color, index) => (
                      <div
                        key={index}
                        className="color-option"
                        style={{
                          backgroundColor: color,
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          border: selectedColor === color ? '3px solid #333' : '2px solid rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => setSelectedColor(color)}
                        title={`Color ${index + 1}`}
                      ></div>
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    Choose a color to distinguish this calendar from others
                  </Form.Text>
                </Form.Group>
                
                <div className="d-grid gap-2">
                  <Button
                    variant="outline-primary"
                    onClick={handleOutlookAuth}
                    disabled={isLoading}
                  >
                    <i className="bi bi-microsoft"></i>
                    <span className="d-none d-md-inline ms-2">Connect Outlook Calendar</span>
                  </Button>
                </div>
              </Form>
            </div>
          </Tab>

          <Tab eventKey="url" title={
            <span>
              <div className="d-flex align-items-center gap-2"><Link/>
              <span className="d-none d-md-inline ms-2">Calendar URL</span>
              </div>
            </span>
          }>
            <div className="p-3">
              <p className="text-muted mb-3">
                Subscribe to any calendar using its iCal/ICS URL.
              </p>
              
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Calendar Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Public Holidays, Sports Events"
                    value={calendarName}
                    onChange={(e) => setCalendarName(e.target.value)}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Calendar Color</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {subscriptionColors.map((color, index) => (
                      <div
                        key={index}
                        className="color-option"
                        style={{
                          backgroundColor: color,
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          border: selectedColor === color ? '3px solid #333' : '2px solid rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => setSelectedColor(color)}
                        title={`Color ${index + 1}`}
                      ></div>
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    Choose a color to distinguish this calendar from others
                  </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Calendar URL</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://example.com/calendar.ics"
                    value={calendarUrl}
                    onChange={(e) => setCalendarUrl(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    Enter the URL of an iCal/ICS calendar feed
                  </Form.Text>
                </Form.Group>
                
                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Adding...' : 'Add Calendar'}
                  </Button>
                </div>
              </Form>
            </div>
          </Tab>
        </Tabs>

        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mt-3">
            {success}
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        {activeTab !== 'url' && (
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading || !calendarName.trim()}
          >
            {isLoading ? 'Adding...' : 'Add Calendar'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SubscriptionModal;
